import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Truck, Lock, User } from 'lucide-react';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        console.log('Login page - Found existing token, verifying...');
        try {
          await axios.get(`${API}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Login page - Token valid, redirecting to admin');
          navigate('/admin', { replace: true });
        } catch (error) {
          console.log('Login page - Token invalid, clearing');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_token_time');
        }
      }
    };
    checkExistingAuth();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (response.data.access_token) {
        // Save token to localStorage with timestamp
        localStorage.setItem('admin_token', response.data.access_token);
        localStorage.setItem('admin_token_time', Date.now().toString());
        
        console.log('✓ Token saved to localStorage:', response.data.access_token.substring(0, 20) + '...');
        
        toast.success("Login erfolgreich!", {
          description: "Willkommen zurück!"
        });
        
        // Small delay to ensure localStorage is saved
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Redirect to admin
        navigate('/admin', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error("Login fehlgeschlagen", {
        description: error.response?.data?.detail || "Benutzername oder Passwort falsch"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 text-[#2C3E50] hover:text-[#FDB913] transition-colors">
            <Truck className="h-12 w-12" />
            <div className="text-left">
              <h1 className="text-2xl font-bold">Hogir Acar</h1>
              <p className="text-sm text-gray-600">Maschinen- & Fahrzeugverleih</p>
            </div>
          </Link>
        </div>

        <Card className="border-2 shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-[#FDB913] rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-[#1a1a1a]" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center text-[#2C3E50]">Admin Login</CardTitle>
            <CardDescription className="text-center">
              Melden Sie sich an, um auf den Admin-Bereich zuzugreifen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="pl-10 border-2 focus:border-[#FDB913]"
                    placeholder="Benutzername"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="pl-10 border-2 focus:border-[#FDB913]"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] font-bold py-6 text-lg transition-all hover:scale-105"
              >
                {loading ? 'Wird angemeldet...' : 'Anmelden'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-600 hover:text-[#FDB913] transition-colors">
                ← Zurück zur Startseite
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
